import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Globe, Heart } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useLang } from "../i18n/LanguageContext";
import { LOGO_URL } from "../assets/images";

export default function Header() {
  const { t, lang, setLang, langs } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const links = [
    { to: "/", label: t.nav.home, end: true },
    { to: "/sobre", label: t.nav.about },
    { to: "/comunidades", label: t.nav.communities || "Comunidades" },
    { to: "/espiritualidade", label: t.nav.spirituality },
    { to: "/eventos", label: t.nav.events },
    { to: "/contactos", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[hsl(var(--border))]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-page flex items-center justify-between h-20">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="header-logo-link"
          aria-label="CIEES Portugal"
        >
          <img
            src={LOGO_URL}
            alt="CIEES Portugal"
            className="h-12 w-12 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="display-heading text-lg text-[hsl(var(--primary))]">CIEES</span>
            <span className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--muted-foreground))]">
              Portugal
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide link-underline ${
                  isActive
                    ? "text-[hsl(var(--primary))] font-medium"
                    : "text-[hsl(var(--foreground))]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/apoiar"
            data-testid="header-support-button"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-[hsl(var(--accent))] text-[#1A201F] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#B3922E] transition-colors"
          >
            <Heart className="h-3.5 w-3.5" />
            {t.nav.support}
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-switcher-trigger"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[hsl(var(--border))] text-xs uppercase tracking-[0.18em] text-[hsl(var(--foreground))] hover:border-[hsl(var(--primary))] transition-colors"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang.toUpperCase()}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              {langs.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={`cursor-pointer ${
                    lang === l.code ? "text-[hsl(var(--primary))] font-medium" : ""
                  }`}
                >
                  <span className="w-7 text-xs uppercase tracking-widest">{l.label}</span>
                  <span className="text-sm">{l.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            className="lg:hidden p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[hsl(var(--border))] bg-[#FDFBF7]" data-testid="mobile-menu">
          <div className="container-page py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={closeMobile}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[hsl(var(--primary))] font-medium" : "text-[hsl(var(--foreground))]"}`
                }
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/apoiar"
              onClick={closeMobile}
              data-testid="mobile-nav-link-apoiar"
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[hsl(var(--accent))] text-[#1A201F] text-xs uppercase tracking-[0.22em] font-semibold"
            >
              <Heart className="h-4 w-4" />
              {t.nav.support}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
